import React, { useState, useRef, useEffect, useCallback } from 'react';
import { createPortal } from 'react-dom';
import SafeIcon from '../SafeIcon';
import { 
  FiBold, FiItalic, FiUnderline, FiZap, FiX, FiCheck, FiLoader, 
  FiAlignLeft, FiAlignCenter, FiAlignRight 
} from 'react-icons/fi';

const AI_TONES = [
  { label: 'Professional', prompt: 'Rewrite this text in a professional, polished tone' },
  { label: 'Casual', prompt: 'Rewrite this text in a casual, friendly tone' },
  { label: 'Persuasive', prompt: 'Rewrite this text to be more persuasive and compelling' },
  { label: 'Shorter', prompt: 'Make this text shorter and more concise' },
  { label: 'Exciting', prompt: 'Rewrite this text to be more exciting and energetic' },
];

const AIPanel = ({ text, onApply, onClose }) => {
  const [loading, setLoading] = useState(false);
  const [result, setResult] = useState('');
  const [error, setError] = useState('');
  const [activeTone, setActiveTone] = useState(null);

  const rewrite = async (tone) => {
    setLoading(true);
    setError('');
    setResult('');
    setActiveTone(tone.label);
    try {
      const res = await fetch('https://api.openai.com/v1/chat/completions', {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          Authorization: `Bearer ${import.meta.env.VITE_OPENAI_API_KEY || ''}`,
        },
        body: JSON.stringify({
          model: 'gpt-3.5-turbo',
          messages: [
            {
              role: 'user',
              content: `${tone.prompt}. Only return the rewritten text, no explanations:\n\n"${text}"`,
            },
          ],
          max_tokens: 300,
          temperature: 0.7,
        }),
      });

      if (!res.ok) throw new Error('AI request failed');
      const data = await res.json();
      setResult(data.choices?.[0]?.message?.content?.trim() || '');
    } catch {
      setError('AI unavailable. Check your OpenAI API key in .env (VITE_OPENAI_API_KEY).');
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="mt-3 border-t border-gray-700/50 pt-3 space-y-2">
      <p className="text-[10px] font-black text-gray-400 uppercase tracking-widest mb-2">
        AI Rewrite
      </p>
      <div className="flex flex-wrap gap-1.5">
        {AI_TONES.map((tone) => (
          <button
            key={tone.label}
            onPointerDown={(e) => { e.stopPropagation(); rewrite(tone); }}
            disabled={loading}
            className={`px-2.5 py-1 rounded-lg text-[10px] font-black uppercase tracking-wider transition-all ${activeTone === tone.label && loading ? 'bg-teal-500 text-white' : 'bg-gray-700 text-gray-300 hover:bg-gray-600'}`}
          >
            {activeTone === tone.label && loading ? '...' : tone.label}
          </button>
        ))}
      </div>
      {error && (
        <p className="text-[10px] text-red-400 font-medium leading-relaxed">{error}</p>
      )}
      {result && (
        <div className="bg-gray-800 rounded-xl p-3 space-y-2">
          <p className="text-xs text-gray-200 leading-relaxed">{result}</p>
          <div className="flex space-x-2 pt-1">
            <button
              onPointerDown={(e) => { e.stopPropagation(); onApply(result); onClose(); }}
              className="flex items-center space-x-1 px-3 py-1.5 bg-teal-500 text-white rounded-lg text-[10px] font-black uppercase tracking-wider hover:bg-teal-400 transition-colors"
            >
              <SafeIcon icon={FiCheck} className="w-3 h-3" />
              <span>Apply</span>
            </button>
            <button
              onPointerDown={(e) => { e.stopPropagation(); setResult(''); setActiveTone(null); }}
              className="px-3 py-1.5 bg-gray-700 text-gray-300 rounded-lg text-[10px] font-black uppercase tracking-wider hover:bg-gray-600 transition-colors"
            >
              Dismiss
            </button>
          </div>
        </div>
      )}
    </div>
  );
};

const EditableText = ({
  initialText,
  initialStyles = {},
  onStyleChange,
  onChange,
  className,
  tag: Tag = 'div',
  isReadOnly = false,
}) => {
  const [isEditing, setIsEditing] = useState(false);
  const [text, setText] = useState(initialText || '');
  const [showAI, setShowAI] = useState(false);
  const [styles, setStyles] = useState({
    color: initialStyles.color || '#111827',
    fontWeight: initialStyles.fontWeight || '900',
    fontStyle: initialStyles.fontStyle || 'normal',
    textDecoration: initialStyles.textDecoration || 'none',
    fontSize: initialStyles.fontSize || 48,
    textAlign: initialStyles.textAlign || 'center',
  });

  const textareaRef = useRef(null);
  const containerRef = useRef(null);
  const toolbarRef = useRef(null);
  const committedRef = useRef(false);

  const [toolbarPos, setToolbarPos] = useState({ top: 0, left: 0, transform: 'translate(-50%, -100%)' });

  useEffect(() => {
    setText(initialText || '');
  }, [initialText]);

  useEffect(() => {
    if (initialStyles && Object.keys(initialStyles).length > 0) {
      setStyles((prev) => ({ ...prev, ...initialStyles }));
    }
  }, [
    initialStyles.fontSize,
    initialStyles.color,
    initialStyles.fontWeight,
    initialStyles.textAlign,
    initialStyles.fontStyle,
    initialStyles.textDecoration,
  ]);

  useEffect(() => {
    if (isEditing && textareaRef.current) {
      const el = textareaRef.current;
      el.style.height = 'auto';
      el.style.height = el.scrollHeight + 'px';
      el.focus();
      el.setSelectionRange(el.value.length, el.value.length);
    }
  }, [isEditing]);

  const updateToolbarPos = useCallback(() => {
    if (!containerRef.current) return;
    const rect = containerRef.current.getBoundingClientRect();
    let top = rect.top - 15;
    let left = rect.left + rect.width / 2;
    let transform = 'translate(-50%, -100%)';

    if (top - 200 < 0) {
      top = rect.bottom + 15;
      transform = 'translate(-50%, 0)';
    }

    if (left < 220) left = 220;
    if (left > window.innerWidth - 220) left = window.innerWidth - 220;

    setToolbarPos({ top, left, transform });
  }, []);

  useEffect(() => {
    if (!isEditing) return;
    updateToolbarPos();
    window.addEventListener('scroll', updateToolbarPos, true);
    window.addEventListener('resize', updateToolbarPos);
    return () => {
      window.removeEventListener('scroll', updateToolbarPos, true);
      window.removeEventListener('resize', updateToolbarPos);
    };
  }, [isEditing, updateToolbarPos]);

  const commit = useCallback((finalText) => {
    if (committedRef.current) return;
    committedRef.current = true;
    setIsEditing(false);
    setShowAI(false);

    const val = finalText ?? text;
    if (onChange && val !== initialText) {
      setTimeout(() => {
        try { onChange(val); } catch (e) { console.error(e); }
      }, 0);
    }
    setTimeout(() => { committedRef.current = false; }, 100);
  }, [text, onChange, initialText]);

  useEffect(() => {
    if (!isEditing) return;
    const handler = (e) => {
      const inContainer = containerRef.current?.contains(e.target);
      const inToolbar = toolbarRef.current?.contains(e.target);
      if (!inContainer && !inToolbar) commit();
    };
    document.addEventListener('pointerdown', handler, true);
    return () => document.removeEventListener('pointerdown', handler, true);
  }, [isEditing, commit]);

  const updateStyle = (newStyles) => {
    const updated = { ...styles, ...newStyles };
    setStyles(updated);
    if (onStyleChange) {
      try { onStyleChange(updated); } catch (e) { console.error(e); }
    }
  };

  const toggle = (prop, on, off = 'normal') => updateStyle({ [prop]: styles[prop] === on ? off : on });

  const handleTextChange = (e) => {
    const val = e.target.value;
    setText(val);
    e.target.style.height = 'auto';
    e.target.style.height = e.target.scrollHeight + 'px';
  };

  const handleKeyDown = (e) => {
    if (e.key === 'Escape') {
      commit();
    }
  };

  const toolbar = isEditing && !isReadOnly ? createPortal(
    <div
      ref={toolbarRef}
      className="fixed bg-gray-900 text-white rounded-2xl shadow-2xl p-4 border border-gray-700 z-[2147483647]"
      style={{
        top: toolbarPos.top,
        left: toolbarPos.left,
        transform: toolbarPos.transform,
        minWidth: 400,
        maxWidth: 450,
      }}
      onPointerDown={(e) => e.stopPropagation()}
    >
      <div className="flex items-center justify-between border-b border-gray-700/50 pb-3 mb-3">
        <div className="flex items-center space-x-1">
          <button onPointerDown={(e) => { e.stopPropagation(); toggle('fontWeight', '900', '400'); }} className={`p-1.5 rounded-lg hover:bg-gray-800 transition-colors ${styles.fontWeight === '900' ? 'text-teal-400 bg-gray-800' : 'text-gray-400'}`}>
            <SafeIcon icon={FiBold} className="w-4 h-4" />
          </button>
          <button onPointerDown={(e) => { e.stopPropagation(); toggle('fontStyle', 'italic', 'normal'); }} className={`p-1.5 rounded-lg hover:bg-gray-800 transition-colors ${styles.fontStyle === 'italic' ? 'text-teal-400 bg-gray-800' : 'text-gray-400'}`}>
            <SafeIcon icon={FiItalic} className="w-4 h-4" />
          </button>
          <button onPointerDown={(e) => { e.stopPropagation(); toggle('textDecoration', 'underline', 'none'); }} className={`p-1.5 rounded-lg hover:bg-gray-800 transition-colors ${styles.textDecoration === 'underline' ? 'text-teal-400 bg-gray-800' : 'text-gray-400'}`}>
            <SafeIcon icon={FiUnderline} className="w-4 h-4" />
          </button>
          
          <div className="w-[1px] h-4 bg-gray-700 mx-1"></div>
          
          <button onPointerDown={(e) => { e.stopPropagation(); updateStyle({ textAlign: 'left' }); }} className={`p-1.5 rounded-lg hover:bg-gray-800 transition-colors ${styles.textAlign === 'left' ? 'text-teal-400 bg-gray-800' : 'text-gray-400'}`}>
            <SafeIcon icon={FiAlignLeft} className="w-4 h-4" />
          </button>
          <button onPointerDown={(e) => { e.stopPropagation(); updateStyle({ textAlign: 'center' }); }} className={`p-1.5 rounded-lg hover:bg-gray-800 transition-colors ${styles.textAlign === 'center' ? 'text-teal-400 bg-gray-800' : 'text-gray-400'}`}>
            <SafeIcon icon={FiAlignCenter} className="w-4 h-4" />
          </button>
          <button onPointerDown={(e) => { e.stopPropagation(); updateStyle({ textAlign: 'right' }); }} className={`p-1.5 rounded-lg hover:bg-gray-800 transition-colors ${styles.textAlign === 'right' ? 'text-teal-400 bg-gray-800' : 'text-gray-400'}`}>
            <SafeIcon icon={FiAlignRight} className="w-4 h-4" />
          </button>
        </div>
        <div className="flex items-center space-x-3 border-l border-gray-700/50 pl-3">
          <label className="flex items-center space-x-2 cursor-pointer group text-[10px] font-bold text-gray-400 uppercase tracking-widest">
            <span>Color</span>
            <div className="relative w-7 h-7 rounded-full border-2 border-gray-600 group-hover:border-teal-400 transition-colors shadow-sm overflow-hidden" style={{ backgroundColor: styles.color }}>
              <input type="color" value={styles.color} onChange={(e) => updateStyle({ color: e.target.value })} className="absolute inset-0 opacity-0 cursor-pointer w-full h-full" />
            </div>
          </label>
          <button onPointerDown={(e) => { e.stopPropagation(); setShowAI((v) => !v); }} className={`flex items-center space-x-1 px-2.5 py-1.5 rounded-lg text-[10px] font-black uppercase tracking-wider transition-all ${showAI ? 'bg-teal-500 text-white' : 'bg-gray-700 text-gray-300 hover:bg-gray-600'}`}>
            <SafeIcon icon={FiZap} className="w-3 h-3" />
            <span>AI</span>
          </button>
        </div>
      </div>
      <div className="flex flex-col space-y-2 mb-1">
        <div className="flex justify-between text-[10px] text-gray-400 font-bold uppercase tracking-widest">
          <span>Font Size</span>
          <span className="text-teal-400">{styles.fontSize}px</span>
        </div>
        <input type="range" min="12" max="120" value={styles.fontSize} onChange={(e) => updateStyle({ fontSize: parseInt(e.target.value) })} className="w-full h-1.5 bg-gray-700 rounded-lg appearance-none cursor-pointer accent-teal-400" />
      </div>
      {showAI && <AIPanel text={text} onApply={(newText) => { setText(newText); }} onClose={() => setShowAI(false)} />}
    </div>,
    document.body
  ) : null;

  const textStyle = {
    color: styles.color,
    fontWeight: styles.fontWeight,
    fontStyle: styles.fontStyle,
    textDecoration: styles.textDecoration,
    fontSize: `${styles.fontSize}px`,
    textAlign: styles.textAlign,
    lineHeight: '1.2',
  };

  return (
    <div ref={containerRef} className="relative w-full">
      {toolbar}
      {isEditing && !isReadOnly ? (
        <textarea
          ref={textareaRef}
          value={text}
          onChange={handleTextChange}
          onKeyDown={handleKeyDown}
          spellCheck={false}
          autoCorrect="off"
          autoCapitalize="off"
          className={`${className} w-full bg-transparent resize-none outline-none ring-2 ring-teal-400/50 rounded-lg px-2 py-1 cursor-text`}
          style={{ ...textStyle, outline: 'none', border: 'none', overflow: 'hidden', minHeight: 40 }}
        />
      ) : (
        <Tag
          onPointerDown={(e) => {
            if (isReadOnly) return;
            e.stopPropagation();
            setIsEditing(true);
          }}
          style={textStyle}
          className={`${className} ${isReadOnly ? 'cursor-default' : 'cursor-pointer hover:bg-teal-50/5'} transition-colors p-2 whitespace-pre-wrap w-full block`}
        >
          {text || (!isReadOnly && <span className="opacity-40">Click to edit...</span>)}
        </Tag>
      )}
    </div>
  );
};

export default EditableText;